/**
 * Sequential FHIR transaction uploader.
 *
 * Every bundle produced by {@link buildBundles} is a self-contained
 * `transaction` bundle. Observation bundles refer to the Patient/Device by
 * conditional URL (`Patient?identifier=<sys>|<participant>`), so the server
 * resolves the references during transaction processing and the client never
 * needs to substitute server-assigned ids.
 *
 * Bundles are uploaded in order: the core bundle first (so Patient and
 * Device exist before any observation bundle tries to reference them),
 * then the observation bundles. Per-bundle failures are reported through
 * `onProgress` and in the result; the upload does not abort on a single
 * failed bundle so the user can retry the failed one without re-uploading
 * everything.
 *
 * Runs in any environment with `fetch`. Used by the browser UI; also
 * reusable from Node tests.
 */

import type { BundleFile } from "../fhir/bundle.js";

export interface UploadOptions {
  baseUrl: string;
  token: string;
  bundles: BundleFile[];
  /** Emitted after every bundle upload attempt (success or failure). */
  onProgress?: (event: UploadProgress) => void;
  /** Optional fetch implementation – handy for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export type UploadProgress =
  | {
      type: "bundle";
      filename: string;
      phase: BundleFile["phase"];
      completed: number;
      total: number;
      httpStatus: number;
      created: number;
      existed: number;
      failed: number;
    }
  | { type: "error"; filename: string; message: string };

export interface UploadBundleResult {
  filename: string;
  phase: BundleFile["phase"];
  httpStatus: number;
  created: number;
  existed: number;
  failed: number;
}

export interface UploadResult {
  bundles: UploadBundleResult[];
  totals: { created: number; existed: number; failed: number };
}

/**
 * Upload every bundle in order. The core bundle goes first so Patient/Device
 * exist by the time observation bundles try to resolve their conditional
 * references. A failure on any single bundle is reported but does not abort
 * the rest of the upload.
 */
export async function uploadBundles(opts: UploadOptions): Promise<UploadResult> {
  const { baseUrl, token, bundles } = opts;
  const fetchImpl = opts.fetchImpl ?? fetch;
  const onProgress = opts.onProgress ?? (() => {});

  // Sort core first so Patient/Device exist before any observation bundle
  // tries to resolve "Patient?identifier=..." server-side. Within the core
  // phase, sort by filename so patient (bundle-001) always precedes device
  // (bundle-002) regardless of the caller's array order.
  const core = bundles.filter((b) => b.phase === "core")
    .sort((a, b) => a.filename.localeCompare(b.filename));
  const ordered = [
    ...core,
    ...bundles.filter((b) => b.phase !== "core"),
  ];

  const results: UploadBundleResult[] = [];
  let completed = 0;
  for (const bundle of ordered) {
    completed++;
    try {
      const res = await postBundle(fetchImpl, baseUrl, token, bundle.json);
      const stats = summariseBatchResponse(res.body);
      // The whole transaction failed (most commonly auth or validation):
      // surface that as a single "failed" entry so the user notices.
      // HAPI returns 4xx/5xx with an OperationOutcome rather than a
      // partial response bundle in that case.
      if (!res.ok) {
        results.push({
          filename: bundle.filename,
          phase: bundle.phase,
          httpStatus: res.status,
          created: 0,
          existed: 0,
          failed: bundle.bundle.entry.length || 1,
        });
        onProgress({
          type: "error",
          filename: bundle.filename,
          message: `HTTP ${res.status} ${res.statusText}: ${res.body.slice(0, 256)}`,
        });
        continue;
      }
      results.push({
        filename: bundle.filename,
        phase: bundle.phase,
        httpStatus: res.status,
        ...stats,
      });
      onProgress({
        type: "bundle",
        filename: bundle.filename,
        phase: bundle.phase,
        completed,
        total: ordered.length,
        httpStatus: res.status,
        ...stats,
      });
    } catch (err) {
      const message = (err as Error).message ?? String(err);
      results.push({
        filename: bundle.filename,
        phase: bundle.phase,
        httpStatus: 0,
        created: 0,
        existed: 0,
        failed: bundle.bundle.entry.length || 1,
      });
      onProgress({ type: "error", filename: bundle.filename, message });
    }
  }

  const totals = results.reduce(
    (acc, r) => ({
      created: acc.created + r.created,
      existed: acc.existed + r.existed,
      failed: acc.failed + r.failed,
    }),
    { created: 0, existed: 0, failed: 0 },
  );

  return { bundles: results, totals };
}

async function postBundle(
  fetchImpl: typeof fetch,
  baseUrl: string,
  token: string,
  body: string,
): Promise<{ ok: boolean; status: number; statusText: string; body: string }> {
  const url = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const res = await fetchImpl(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/fhir+json",
      Accept: "application/fhir+json",
      Authorization: `Bearer ${token}`,
    },
    body,
  });
  const text = await res.text();
  return { ok: res.ok, status: res.status, statusText: res.statusText, body: text };
}

/**
 * Count `2xx` vs `4xx`/`5xx` vs "already existed" responses in a
 * transaction-response (or batch-response) bundle. Each entry's response
 * status is read independently; if the whole HTTP response is a 4xx/5xx
 * (i.e. the transaction was rejected) the caller should treat the whole
 * bundle as failed instead.
 */
export function summariseBatchResponse(
  bodyText: string,
): { created: number; existed: number; failed: number } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(bodyText);
  } catch {
    return { created: 0, existed: 0, failed: 0 };
  }
  const entries = (parsed as { entry?: Array<{ response?: { status?: string } }> }).entry ?? [];
  let created = 0;
  let existed = 0;
  let failed = 0;
  for (const e of entries) {
    const status = e.response?.status ?? "";
    const code = Number(status.split(" ")[0]);
    if (code === 201) created++;
    else if (code === 200) existed++;
    else if (code >= 400) failed++;
  }
  return { created, existed, failed };
}
